"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { useProjectStore } from "@/stores/useProjectStore";
import { useCurrentUserSharesStore } from "@/stores/useCurrentUserSharesStore";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorMessage } from "@/components/ui/error-message";
import { WorkspaceProjectsGrid } from "@/components/workspace/WorkspaceProjectsGrid";
import { getErrorMessage } from "@/types/problem-detail";
export default function Page() {
  const {
    projects,
    loading,
    fetchProjects,
    createProject,
    deleteProject
  } = useProjectStore();
  const isShared = useCurrentUserSharesStore(s => s.isShared);
  const ownedProjects = projects.filter(p => !isShared("project", p.id));
  const sharedProjects = projects.filter(p_0 => isShared("project", p_0.id));
  const router = useRouter();
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewProjectName("");
    setCreateError(null);
  };
  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name) return;
    setCreateError(null);
    setCreating(true);
    try {
      const created = await createProject(name);
      handleCloseCreateDialog();
      if (created?.id) {
        sessionStorage.setItem(`collaborate-nudge-pending-${created.id}`, "1");
        router.push(`/dashboard/projects/${created.id}?collaborate=1`);
      }
    } catch (error) {
      const message = getErrorMessage(error, "Failed to create project");
      setCreateError(message);
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };
  return <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex flex-1 flex-col justify-center px-4 py-10 md:px-8 md:py-16">
        <WorkspaceProjectsGrid ownedProjects={ownedProjects} sharedProjects={sharedProjects} loading={loading} onCreateProject={() => setCreateDialogOpen(true)} onDeleteProject={deleteProject} />
      </div>

      <footer className="flex shrink-0 justify-center border-t border-border/30 px-4 py-3">
        <p className="text-xs text-muted-foreground">
          <a href="https://playbook.swiftcns.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-foreground hover:underline">
            SwiftCNS Innovation Playbook
            <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </footer>

      <Dialog open={createDialogOpen} onOpenChange={open => {
      if (!open) handleCloseCreateDialog();else setCreateDialogOpen(true);
    }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Enter a title for your new project to get started.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {createError && <ErrorMessage message={createError} className="mb-2" />}
            <div className="space-y-2">
              <Label htmlFor="dashboard-project-title">Title</Label>
              <Input id="dashboard-project-title" placeholder="Enter project title" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} onKeyDown={e_0 => {
              if (e_0.key === "Enter" && newProjectName.trim() && !creating) {
                handleCreateProject();
              }
              if (e_0.key === "Escape") handleCloseCreateDialog();
            }} autoFocus disabled={creating} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseCreateDialog} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject} disabled={!newProjectName.trim() || creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>;
}